import { getMilestone } from '../lib/streak';

const J = { fontFamily: "'Josefin Sans', sans-serif" };
const SMALL = { ...J, fontSize: '0.5rem', letterSpacing: '0.1em', textTransform: 'uppercase' };

function getCardState(count, best) {
  if (count === 0)    return 'broken';
  if (count < 7)     return 'approaching';
  if (count === 7)   return 'milestone';
  if (count < 30)    return 'between';
  if (count === 30)  return 'milestone';
  if (count < 100)   return 'between';
  if (count === 100) return 'milestone';
  if (count < 365)   return 'between';
  return 'milestone';
}

function getNextTarget(count) {
  if (count >= 365) return null;
  if (count >= 100) return 365;
  if (count >= 30)  return 100;
  if (count >= 7)   return 30;
  return 7;
}

function getLastMilestoneInfo(count, lastMilestoneKey) {
  if (lastMilestoneKey) {
    const map = { m7: 7, m30: 30, m100: 100, m365: 365 };
    const c = map[lastMilestoneKey];
    return c ? getMilestone(c) : null;
  }
  if (count >= 365) return getMilestone(365);
  if (count >= 100) return getMilestone(100);
  if (count >= 30)  return getMilestone(30);
  if (count >= 7)   return getMilestone(7);
  return null;
}

export default function StreakCard({ streak, onOpenModal }) {
  const { streak_count: count, best_streak: best, last_milestone } = streak;
  const state          = getCardState(count, best);
  const nextTarget     = getNextTarget(count);
  const nextInfo       = nextTarget ? getMilestone(nextTarget) : null;
  const currentMilestone = state === 'milestone' ? getMilestone(count) : null;
  const lastInfo       = state === 'broken'
    ? getLastMilestoneInfo(count, last_milestone)
    : state === 'between' ? getLastMilestoneInfo(count, null) : null;

  const isClickable = state === 'milestone' || state === 'between' || state === 'broken' || state === 'approaching';
  const flameClass  = count >= 100 ? 'flame-pulse' : count >= 30 ? 'flame-glow' : '';

  const borderLeft = state === 'milestone'   ? '3px solid var(--gold)'
                   : state === 'between'    ? '3px solid var(--gold-line)'
                   : state === 'approaching'? '3px solid var(--gold-line)'
                   : state === 'broken'     ? '3px solid var(--warm-line)'
                   : 'none';

  return (
    <div
      onClick={isClickable ? () => onOpenModal(state, currentMilestone || lastInfo) : undefined}
      style={{
        display: 'flex', flexDirection: 'column',
        marginTop: '1rem', padding: '0.6rem 0.9rem',
        background: state === 'broken' ? 'var(--warm-soft)' : 'rgba(229,150,83,0.07)',
        border: state === 'milestone' ? '1px solid var(--gold-line)'
              : state === 'broken'    ? '1px solid var(--warm-line)'
              : '1px solid rgba(229,150,83,0.16)',
        borderLeft,
        borderRadius: 4,
        cursor: isClickable ? 'pointer' : 'default',
      }}
    >
      {/* ── Main row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span
          className={flameClass}
          style={{ fontSize: '1.2rem', display: 'inline-block', opacity: state === 'broken' ? 0.35 : 1 }}
        >🔥</span>

        <div>
          <span style={{
            fontSize: '1.2rem', fontStyle: 'italic',
            color: state === 'broken' ? 'var(--text3)' : 'var(--gold)',
          }}>{count}</span>
          <span style={{ ...J, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold2)', marginLeft: '0.3rem' }}>
            day streak
          </span>
        </div>

        {/* ── Right column ── */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* 7-day milestone: all 7 dots filled */}
          {state === 'milestone' && count === 7 && (
            <div style={{ display: 'flex', gap: '0.3rem' }}>
              {Array(7).fill(null).map((_, i) => <div key={i} className="streak-day done" />)}
            </div>
          )}

          {/* 30/100/365 milestone: Arabic title */}
          {state === 'milestone' && count !== 7 && currentMilestone && (
            <span style={{ fontSize: '1rem', color: 'var(--gold)' }}>
              {currentMilestone.arabic}
            </span>
          )}

          {/* Between: faint next Arabic + week-progress dots */}
          {state === 'between' && (
            <>
              {nextInfo && (
                <span style={{ fontSize: '0.7rem', color: 'var(--gold2)', opacity: 0.45 }}>
                  {nextInfo.arabic}
                </span>
              )}
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                {Array(7).fill(null).map((_, i) => (
                  <div key={i} className={`streak-day ${i < count % 7 ? 'done' : ''}`} />
                ))}
              </div>
            </>
          )}

          {/* Approaching: faint next Arabic + dots filling progressively */}
          {state === 'approaching' && (
            <>
              {nextInfo && (
                <span style={{ fontSize: '0.7rem', color: 'var(--gold2)', opacity: 0.45 }}>
                  {nextInfo.arabic}
                </span>
              )}
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                {Array(7).fill(null).map((_, i) => (
                  <div key={i} className={`streak-day ${i < count ? 'done' : ''}`} />
                ))}
              </div>
            </>
          )}

          {/* Broken: faint last Arabic title */}
          {state === 'broken' && lastInfo && (
            <span style={{ fontSize: '0.7rem', color: 'var(--text3)', opacity: 0.4 }}>
              {lastInfo.arabic}
            </span>
          )}
        </div>
      </div>

      {/* ── Tap hint (milestone) ── */}
      {state === 'milestone' && (
        <div style={{ ...SMALL, color: 'var(--gold2)', textAlign: 'right', marginTop: '0.3rem' }}>
          TAP TO VIEW MILESTONE ›
        </div>
      )}

      {/* ── Progress bar (approaching + between) ── */}
      {(state === 'approaching' || state === 'between') && nextTarget && (
        <div style={{ marginTop: '0.45rem' }}>
          <div className="streak-progress-track">
            <div className="streak-progress-fill" style={{ width: `${Math.min(100, (count / nextTarget) * 100)}%` }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.2rem' }}>
            <span style={{ ...SMALL, color: 'var(--gold2)' }}>To {nextInfo?.arabic}</span>
            <span style={{ ...SMALL, color: 'var(--text3)' }}>{nextTarget - count} days</span>
          </div>
        </div>
      )}

      {/* ── Beat your best row (active streak below personal best) ── */}
      {(state === 'approaching' || state === 'between') && best > count && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: '0.4rem', paddingTop: '0.35rem',
          borderTop: '1px solid rgba(229,150,83,0.12)',
        }}>
          <div style={{ ...J, fontSize: '0.5rem', letterSpacing: '0.06em', color: 'var(--text3)' }}>
            Beat your best
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.2rem' }}>
            <span style={{ ...J, fontSize: '0.5rem', color: 'var(--text3)' }}>{best - count} days to go ·</span>
            <span style={{ fontSize: '0.85rem', color: 'var(--gold)', fontStyle: 'italic' }}>{best}</span>
            <span style={{ ...SMALL, color: 'var(--gold2)' }}>best</span>
          </div>
        </div>
      )}

      {/* ── Personal best row (broken) ── */}
      {state === 'broken' && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: '0.5rem', paddingTop: '0.4rem',
          borderTop: '1px solid var(--warm-soft)',
        }}>
          <div style={{ ...J, fontSize: '0.55rem', letterSpacing: '0.06em', color: 'var(--text3)' }}>
            {best > 0 ? 'Your best streak was' : 'Start your streak today'}
          </div>
          {best > 0 && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
              <span style={{ fontSize: '1.1rem', color: 'var(--gold)', fontStyle: 'italic' }}>{best}</span>
              <span style={{ ...SMALL, color: 'var(--gold2)' }}>days</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
